<template>
  <div class="web-app-container">
    <div class="flex-row">
      <v-spacer />
      <v-btn
        @click="showAddScreenDialog = true"
        outline
        small>
        <v-icon
          small
          left>
          fas fa-plus
        </v-icon>
        {{ $t('wdm.views.webAppEdition.labels.addScreen', 'Add screen') }}
      </v-btn>
    </div>
    <we-detail-view
      v-if="webAppDetail"
      class="detail"
      display-in="designer"
      :item="webAppDetail"
      :schema="schema"
      :edit-mode="false" />

    <v-dialog
      v-model="showWebAppEditionDialog"
      scrollable
      persistent>
      <v-card v-if="showWebAppEditionDialog">
        <v-card-title>
          <h2 class="text-no-wrap">
            {{ $t('wdm.views.webAppEdition.labels.editWebApp', 'Edit WebApp') }} {{ editedDefinition.item.label ? `: ${editedDefinition.item.label}` : '' }}
          </h2>
          <v-spacer />
          <v-icon
            class="close-icon"
            @click="showWebAppEditionDialog = false"
            small>
            fas fa-times
          </v-icon>
        </v-card-title>
        <v-card-text class="dialog-content-text">
          <WebAppEditionWizard
            :item="editedDefinition.item"
            :show.sync="showWebAppEditionDialog" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showAddScreenDialog"
      scrollable
      persistent>
      <v-card v-if="showAddScreenDialog">
        <v-card-title>
          <h2>
            {{ $t('wdm.views.webAppEdition.labels.addScreen', 'Add screen') }}
          </h2>
          <v-spacer />
          <v-icon
            @click="showAddScreenDialog = false"
            small>
            fas fa-times
          </v-icon>
        </v-card-title>
        <v-card-text class="dialog-content-text">
          <TemplateSelector
            :selected-template="selectedScreen"
            @click="selectScreen" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            :disabled="!selectedScreen"
            :loading="addingScreen"
            outline
            @click="addSelectedScreenToSheet">
            Ok
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import { mapState, mapActions } from 'vuex'
import { webAppDataSource, webAppSchema } from 'server-model'

export default {
  asyncComputed: {
    webAppDetail () {
      return this.currentWebApp && webAppDataSource.detail(this.currentWebApp.id)
    }
  },
  components: {
    WebAppEditionWizard: () => import(/* webpackChunkName: "WebAppEditionWizard" */ 'server-model/src/components/WebAppEditionWizard'),
    TemplateSelector: () => import(/* webpackChunkName: "TemplateSelector" */ './TemplateSelector')
  },
  data () {
    return {
      schema: webAppSchema,
      addingScreen: false,
      selectedScreen: null,
      showAddScreenDialog: false,
      saving: false
    }
  },
  computed: {
    ...mapState('wdm', [
      'currentWebApp'
    ]),
    ...mapState('schema-view', [
      'editedDefinition'
    ]),
    showWebAppEditionDialog: {
      get () {
        return this.editedDefinition && this.editedDefinition.item
      },
      set (value) {
        if (!value) {
          this.editDefinition(null)
        }
      }
    }
  },
  methods: {
    ...mapActions('office', [
      'runCommand'
    ]),
    ...mapActions('schema-view', [
      'editDefinition'
    ]),
    selectScreen (item) {
      this.selectedScreen = item
    },
    async addSelectedScreenToSheet () {
      try {
        this.addingScreen = true
        await this.addToSheet(this.selectedScreen)
        this.showAddScreenDialog = false
      } finally {
        this.addingScreen = false
      }
    },
    async addToSheet (item) {
      const response = await axios.get(item.fileUrl, {
        responseType: 'arraybuffer'
      })
      const base64 = Buffer.from(response.data, 'binary').toString('base64')
      return this.loadBase64(base64)
    },
    loadBase64 (base64) {
      const startIndex = base64.indexOf('base64,')
      const workbookContents = startIndex !== -1 ? base64.substr(startIndex + 7) : base64

      return this.runCommand(async (context, office) => {
        const sheets = office.workbook.worksheets
        sheets.addFromBase64(
          workbookContents,
          null, // get all the worksheets
          // eslint-disable-next-line no-undef
          Excel.WorksheetPositionType.after, // insert them after the worksheet specified by the next parameter
          sheets.getActiveWorksheet() // insert them after the active worksheet
        )
        await office.sync()
      })
    }
  }
}
</script>

<style lang="scss">
  @import "~common-assets/scss/we";
  @include media-breakpoint-only(xs) {
    .v-dialog {
      margin: 5px;
    }
  }
  @include media-breakpoint-up(sm) {
    .v-dialog {
      margin: 90px;
    }
  }
  .v-stepper__content {
    padding-left: 5px !important;
    padding-right: 35px !important;
  }
</style>

<style lang="scss" scoped>
  @import "~common-assets/scss/we";
  .web-app-container {
    @extend .full-size;
    overflow: hidden;
    .detail {
      background: transparent;
      margin: 0;
    }
  }
  .dialog-content-text {
    height: 100%;
    overflow-x: hidden;
    padding: 0;
  }
  .close-icon {
    position: absolute;
    right: 10px;
    top: 10px;
  }
</style>
